import {Params} from 'react-router-dom';
import _get from 'lodash-es/get';
import omit from 'lodash-es/omit';
import {produce} from 'immer';
import dayjs from 'dayjs';

import {
  ClaimIntentions,
  DocumentsInfo,
  DocumentsPartName,
  DocumentUuidEnum,
  ProductSheetActions,
  ProductSheetState,
  ResolveContextsParams,
  SectionMapping,
  SectionName,
  SectionsPartName
} from '@common/types/stores/productSheetStore.types';
import {apiEditClaimUsage, apiGenerateClaims, apiPutInDraft, apiReplaceClaim, apiResolveContextsUuid} from '@modules/piu/api/apiProducts';
import {
  apiApproveReview,
  apiAskForRewiew,
  apiAskForScoring,
  apiBypassScoring,
  apiDuplicateGmc,
  apiEditSection,
  apiPutClaimInDraft,
  apiRejectReview,
  apiScoringDone,
  apiSetClaimScoring
} from '@modules/piu/api/apiGlobalMarketingContents';
import useAuthenticationStore from '@common/stores/authenticationStore';
import JwtScope from '@common/constants/authentication/JwtScopeConstants';
import {mapSectionData} from '@modules/piu/features/productSheet/helpers/mapSectionData';
import {
  GmcFormValues,
  DuplicateGmcFormValues,
  EditProductInformationFormValues,
  SetClaimScoringFormValues,
  RmcFormValue,
  EditRmcFormValue,
  SetClaimIntentionScoringFormValues,
  EditClaimFormValues,
  EditClaimUsageFormValues,
  ClaimsFormValue
} from '@common/types/forms/productForms.types';
import documentTypes from '@common/constants/documents/documentTypes';
import {
  apiCreateDocument,
  apiGetDocuments,
  apiEditDocument,
  apiGetValueOptions,
  apiGetDocumentUrlByType,
  apiGetDocumentSection,
  apiGetDocument,
  apiDocumentInformation
} from '@common/api/apiDocuments';
import {ClaimIntention} from '@common/types/documents/globalMarketingContent.types';
import {FilterValue, Filters, Model} from '@common/types/components/datatable.types';
import {CacheDocument} from '@common/types/documents/genericDocument.types';
import {initializePaginationFilters} from '@common/helpers/store/datatableHelper';
import {API_FILTER_AFTER} from '@common/constants/datatable/datatableConstants';
import {mapValueOptionsCompositionLanguagesResponse, mapValueOptionsRegionCountryResponse, mapValueOptionsResponse} from '@common/helpers/valueOptions/valueOptionsMapper';
import {mapGmcValuesForCreate} from '@modules/piu/features/productSheet/helpers/mapGmcValuesForCreate';
import {getEndPoints} from '@common/stores/configStore';
import {createCustomStore} from '@common/helpers/store/storeHelper';
import {Uuid} from '@common/types/index.types';
import {ProductInfo} from '@common/types/documents/product.types';
import {enhanceRegionUuid} from '@modules/piu/features/productSheet/helpers/productSheetHelper';
import {ValueOptionsApiResponse} from '@common/types/documents/valueOptions.types';
import {
  CreateNewTranslationsOrderValues,
  EditTranslationsOrderValues,
  OrderType,
  ValueOptionsCompositionLanguagesApiResponse
} from '@modules/piu/features/productSheet/components/Translations/translationsOrder.type';
import {DATE_FORMAT} from '@common/constants/datesConstants';
import {ORDER_TYPE_LEGAL, translationsOrderTypesPath} from '@modules/piu/features/productSheet/components/Translations/constants';
import {removeEmptyString} from '@common/helpers/form/formValuesHelper';

const initPaginationFiltersState = {
  [API_FILTER_AFTER]: ''
};

export const initialState: ProductSheetState = {
  claims: {},
  claimIntentions: {},
  documents: {
    compare: null,
    current: {
      activeUuid: null,
      country: null,
      gmc: null,
      product: null,
      region: null,
      rmc: null
    }
  },
  isGmcVersionLoading: false,
  mediaData: [],
  mediaFilters: {},
  mediaModels: [],
  reorderedClaimIntentionTitle: '',
  hasReorderedParnuts: false,
  sections: {
    compare: {},
    current: {}
  },
  selectedClaims: [],
  selectedMedia: [],
  valueWizardForm: {
    defaultValue: {name: '', text: '', isLoading: false},
    documentType: '',
    intention: {key: '', label: ''},
    isOpen: false,
    type: ''
  },
  isLoadingResolveContext: false
};

const useProductSheetStore = createCustomStore<ProductSheetState & ProductSheetActions>('productSheet', (set, get) => ({
  ...initialState,

  /**
   * Load the product information and the documents ids + names
   * @param urlSearchParams Parameters from query string
   * @param params Parameters from the url
   */
  loadBaseProductInformation: async (urlSearchParams: URLSearchParams, params: Readonly<Params<string>>) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    set(
      state => {
        state.isLoadingResolveContext = true;
      },
      undefined,
      'loadBaseProductInformation/pending'
    );

    // Get the product information
    const response = await apiGetDocuments(
      endpoints.apiDocuments,
      {
        mainItem: params.productId!,
        typeId: 'product'
      },
      stsCredentials,
      _get(scopedJwts, [JwtScope.WEBAPP])
    );

    if (!response.data.length) {
      throw new Error('Product not found');
    }

    // The api returns an array of one element corresponding to the productId
    const productInformation = response.data[0];
    set(
      state => {
        state.documents.current.product = {
          ...(productInformation as ProductInfo),
          id: productInformation.uuid
        };
      },
      undefined,
      'loadBaseProductInformation/setProductInformation'
    );

    const gmcUuidParam = urlSearchParams.get('version');
    const regionUuidParam = urlSearchParams.get('region');
    const countryUuidParam = urlSearchParams.get('country');

    const resolveContextsParams = {
      product: params.productId ?? '',
      version: gmcUuidParam ?? undefined,
      region: regionUuidParam ?? undefined,
      country: countryUuidParam ?? undefined
    };

    // Resolve the context to get the documents ids & names
    const {data: resolveContextsData} = await apiResolveContextsUuid(endpoints?.apiDocuments, resolveContextsParams, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    const {country, globalMarketingContent, regionalMarketingContent} = resolveContextsData;

    // Store the ids of the documents
    set(
      state => {
        state.documents.current.gmc = globalMarketingContent.id ? {id: globalMarketingContent.id, name: globalMarketingContent.name || ''} : null;
        state.documents.current.rmc = regionalMarketingContent.id ? {id: regionalMarketingContent.id, name: regionalMarketingContent.name || ''} : null;
        state.documents.current.country = country.id ? {id: country.id, name: country.name || ''} : null;
        state.documents.current.region = regionUuidParam ? enhanceRegionUuid(regionUuidParam) : null;

        const activeUuid = regionalMarketingContent?.id ?? globalMarketingContent?.id;
        if (activeUuid) {
          state.documents.current.activeUuid = activeUuid;
        }
      },
      undefined,
      'loadBaseProductInformation/setDocumentsIds'
    );

    set(
      state => {
        state.isLoadingResolveContext = false;
      },
      undefined,
      'loadBaseProductInformation/fulfilled'
    );
  },

  resetProductSheet: () => {
    set(() => initialState, undefined, 'resetProductSheet');
  },

  setIsLoadingResolveContext: (isLoadingResolveContext: boolean) => {
    set(
      state => {
        state.isLoadingResolveContext = isLoadingResolveContext;
      },
      undefined,
      'setIsLoadingResolveContext'
    );
  },

  setProduct: (productInfo: ProductInfo | null) => {
    set(
      state => {
        state.documents.current.product = productInfo;
      },
      undefined,
      'setProduct'
    );
  },

  setDocument: (info: DocumentsInfo | null, key: DocumentUuidEnum.COUNTRY | DocumentUuidEnum.GMC | DocumentUuidEnum.RMC | DocumentUuidEnum.REGION) => {
    set(
      state => {
        state.documents.current[key] = info;
      },
      undefined,
      'setDocument'
    );
  },

  setDocumentUuid: (uuid: Uuid | null, key: DocumentUuidEnum.ACTIVE_UUID) => {
    set(
      state => {
        state.documents.current[key] = uuid || '';
      },
      undefined,
      'setDocumentUuid'
    );
  },

  resetCompare: () => {
    set(
      state => {
        state.documents.compare = null;
        state.sections.compare = {};
      },
      undefined,
      'resetCompare'
    );
  },

  changeCompareProduct: async (product: DocumentsInfo) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    const response = await apiGetDocuments(
      endpoints.apiDocuments,
      {
        uuid: product.id,
        typeId: 'product'
      },
      stsCredentials,
      _get(scopedJwts, [JwtScope.WEBAPP])
    );

    if (!response.data.length) {
      throw new Error('Product not found');
    }

    // The api returns an array of one element corresponding to the productId
    const productInformation = response.data[0];

    set(
      state => {
        if (state.documents.compare) {
          state.documents.compare.product = {
            ...(productInformation as ProductInfo),
            ...product
          };
          state.documents.compare.activeUuid = null;
          state.documents.compare.country = null;
          state.documents.compare.gmc = null;
          state.documents.compare.region = null;
          state.documents.compare.rmc = null;
        }
      },
      undefined,
      'changeCompareProduct'
    );
  },

  changeProduct: (newProduct: ProductInfo, documentsPartName: DocumentsPartName = 'current') => {
    set(
      state => {
        if (state.documents[documentsPartName]) {
          state.documents[documentsPartName].product = newProduct;
          state.documents[documentsPartName].activeUuid = null;
          state.documents[documentsPartName].country = null;
          state.documents[documentsPartName].gmc = null;
          state.documents[documentsPartName].region = null;
          state.documents[documentsPartName].rmc = null;
        }
      },
      undefined,
      'changeProduct'
    );
  },

  changeGmc: (gmc: DocumentsInfo | null, documentsPartName: DocumentsPartName = 'current') => {
    set(
      state => {
        if (state.documents[documentsPartName]) {
          state.documents[documentsPartName].activeUuid = gmc?.id || null;
          state.documents[documentsPartName].country = null;
          state.documents[documentsPartName].gmc = gmc;
          state.documents[documentsPartName].region = null;
          state.documents[documentsPartName].rmc = null;
        } else if (documentsPartName === 'compare') {
          state.documents.compare = {
            activeUuid: gmc?.id || null,
            country: null,
            gmc: gmc,
            product: state.documents.current.product,
            region: null,
            rmc: null
          };
        }
      },
      undefined,
      'changeGmc'
    );
  },

  changeRmc: ({
    country,
    documentsPartName = 'current',
    gmc = null,
    region,
    rmc
  }: {
    country: DocumentsInfo | null;
    documentsPartName?: DocumentsPartName;
    gmc: DocumentsInfo | null;
    region: DocumentsInfo | null;
    rmc: DocumentsInfo | null;
  }) => {
    set(
      state => {
        if (state.documents[documentsPartName]) {
          const newActiveUuid = rmc ? rmc.id : state.documents[documentsPartName].gmc?.id || null;
          state.documents[documentsPartName].activeUuid = newActiveUuid;
          state.documents[documentsPartName].country = country;
          state.documents[documentsPartName].gmc = gmc;
          state.documents[documentsPartName].region = region;
          state.documents[documentsPartName].rmc = rmc;
        } else if (documentsPartName === 'compare') {
          state.documents.compare = {
            activeUuid: rmc ? rmc.id : null,
            country: country,
            gmc: gmc,
            product: state.documents.current.product,
            region: region,
            rmc: rmc
          };
        }
      },
      undefined,
      'changeRmc'
    );
  },

  updateSection: async ({countryUuid, uuid, sectionMapping, data}: {countryUuid?: Uuid; uuid: Uuid; sectionMapping: SectionMapping; data: object}) => {
    const endpoints = getEndPoints();
    const sectionsPart = 'current';
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const {section: sectionName} = sectionMapping;
    const uuidKey = countryUuid ?? uuid;

    set(
      state => {
        if (state.sections[sectionsPart][uuidKey][sectionName]) {
          state.sections[sectionsPart][uuidKey][sectionName].isEditLoading = true;
        }
      },
      undefined,
      'updateSection/pending'
    );

    await apiEditSection({
      additionalBodyParams: {
        ...(countryUuid ? {country: countryUuid} : {})
      },
      baseUrl: endpoints.apiDocuments,
      uuid,
      credentials: stsCredentials,
      scopedJwt: _get(scopedJwts, [JwtScope.WEBAPP]),
      action: sectionMapping.action,
      data
    });

    set(
      state => {
        if (state.sections[sectionsPart][uuidKey][sectionName]) {
          state.sections[sectionsPart][uuidKey][sectionName].isEditLoading = false;
        }
      },
      undefined,
      'updateSection/fulfilled'
    );
  },

  getDocumentSection: async ({countryUuid, uuid, sectionName, sectionsPartName = 'current'}: {countryUuid?: Uuid; uuid: Uuid; sectionName: SectionName; sectionsPartName?: SectionsPartName}) => {
    const endpoints = getEndPoints();
    // If there is a country id, then the provided uuid is a rmc uuid
    const uuidKey = countryUuid ?? uuid;

    set(
      state => {
        if (!state.sections[sectionsPartName][uuidKey]) {
          // First section for this uuid then creates the ProductSections node
          state.sections[sectionsPartName][uuidKey] = {
            [sectionName]: {
              isLoading: true
            }
          };
        } else {
          state.sections[sectionsPartName][uuidKey][sectionName] = {
            isLoading: true
          };
        }
      },
      undefined,
      'getDocumentSection/pending'
    );

    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiGetDocumentSection({
      additionalParamsUrls: {
        ...(countryUuid ? {countryId: countryUuid} : {})
      },
      baseUrl: endpoints.apiDocuments,
      uuid,
      section: sectionName,
      credentials: stsCredentials,
      scopedJwt: _get(scopedJwts, [JwtScope.WEBAPP])
    });
    const mappedData = mapSectionData(sectionName, response);

    set(
      state => {
        state.sections[sectionsPartName][uuidKey][sectionName] = {
          ...state.sections[sectionsPartName][uuidKey][sectionName],
          ...mappedData,
          isLoading: false
        };
      },
      undefined,
      'getDocumentSection/fulfilled'
    );

    return response;
  },

  getDocumentSections: async ({countryUuid, uuid, sectionNames, sectionsPartName = 'current'}: {countryUuid?: Uuid; uuid: Uuid; sectionNames: SectionName[]; sectionsPartName?: SectionsPartName}) => {
    const endpoints = getEndPoints();
    // If there is a country id, then the provided uuid is a rmc uuid
    const uuidKey = countryUuid ?? uuid;

    set(
      state => {
        sectionNames.forEach(sectionName => {
          if (!state.sections[sectionsPartName][uuidKey]) {
            // First section for this uuid then creates the ProductSections node
            state.sections[sectionsPartName][uuidKey] = {
              [sectionName]: {
                isLoading: true
              }
            };
          } else {
            state.sections[sectionsPartName][uuidKey][sectionName] = {
              isLoading: true
            };
          }
        });
      },
      undefined,
      'getDocumentSection/pending'
    );

    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiGetDocumentSection({
      additionalParamsUrls: {
        ...(countryUuid ? {countryId: countryUuid} : {})
      },
      baseUrl: endpoints.apiDocuments,
      uuid,
      section: sectionNames.join(','),
      credentials: stsCredentials,
      scopedJwt: _get(scopedJwts, [JwtScope.WEBAPP])
    });
    const mappedDataArray = sectionNames.map(sectionName => mapSectionData(sectionName, response));

    set(
      state => {
        sectionNames.forEach((sectionName, index) => {
          state.sections[sectionsPartName][uuidKey][sectionName] = {
            ...state.sections[sectionsPartName][uuidKey][sectionName],
            ...mappedDataArray[index],
            isLoading: false
          };
        });
      },
      undefined,
      'getDocumentSection/fulfilled'
    );

    return response;
  },

  resetDocumentSection: ({countryUuid, uuid, sectionNames, sectionsPartName = 'current'}: {countryUuid?: Uuid; uuid: Uuid; sectionNames: SectionName[]; sectionsPartName?: SectionsPartName}) => {
    const uuidKey = countryUuid ?? uuid;
    set(
      state => {
        sectionNames.forEach(sectionName => {
          state.sections[sectionsPartName][uuidKey][sectionName] = undefined;
        });
      },
      undefined,
      'resetDocumentSection'
    );
  },

  createNewGmc: async (product: string, values: GmcFormValues) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiCreateDocument(
      endpoints.apiDocuments,
      {product, type: documentTypes.GLOBAL_MARKETING_CONTENT, ...mapGmcValuesForCreate(values)},
      stsCredentials,
      _get(scopedJwts, [JwtScope.WEBAPP])
    );
    return response;
  },

  duplicateGmc: async (fromGmcUuid: Uuid, values: DuplicateGmcFormValues) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiDuplicateGmc(endpoints.apiDocuments, fromGmcUuid, values, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return response;
  },

  editDocumentInformation: async (uuid: Uuid, values: GmcFormValues | EditRmcFormValue) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiDocumentInformation(endpoints.apiDocuments, uuid, {...values}, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
  },

  setScoringDone: async (documentId: string, data: {claimId: string; country?: Uuid}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiScoringDone(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
  },

  setApproveReview: async (documentId: string, data: {claimId: string; comment?: string}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiApproveReview(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
  },

  setRejectReview: async (documentId: string, data: {claimId: string; comment: string}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiRejectReview(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
  },

  putClaimInDraft: async (documentId: string, data: {claimIds: string[]; country?: Uuid}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiPutClaimInDraft(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
  },

  askForRewiew: async (documentId: string, data: {claimIds: string[]}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiAskForRewiew(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
  },

  setClaimScoring: async (documentId: string, data: SetClaimScoringFormValues | SetClaimIntentionScoringFormValues) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiSetClaimScoring(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
  },

  askForScoring: async (documentId: string, data: {claimIds: string[]; country?: Uuid}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiAskForScoring(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
    set(
      state => {
        state.selectedClaims = [];
      },
      undefined,
      'askForScoring'
    );
  },

  bypassScoring: async (documentId: string, data: {claimIds: string[]; country?: Uuid}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiBypassScoring(endpoints.apiDocuments, documentId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), data);
    set(
      state => {
        state.selectedClaims = [];
      },
      undefined,
      'bypassScoring'
    );
  },

  setSelectedClaimIntentions: (intention: ClaimIntention) => {
    set(
      state =>
        produce(state, draft => {
          if (state.selectedClaims.some(item => item.id === intention.id)) {
            draft.selectedClaims = state.selectedClaims.filter(claim => claim.id !== intention.id);
          } else {
            draft.selectedClaims.push(intention);
          }
        }),
      undefined,
      'setSelectedClaimIntentions'
    );
  },

  setAllClaimsItentions: (data: ClaimIntention[]) => {
    set(
      state => {
        state.selectedClaims = state.selectedClaims.length === data.length ? [] : data;
      },
      undefined,
      'setAllClaimsItentions'
    );
  },

  getDocumentMedia: async (filters: Filters = {}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const {data, facets} = await apiGetDocuments(endpoints.apiDocuments, filters, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    const models = Object.keys(facets || {}).reduce((acc: Model[], facetKey) => {
      acc.push({
        key: facetKey,
        items: _get(facets, [facetKey as string], []).map(({key, label, count}) => ({
          key,
          label: label || key,
          count
        }))
      });
      return acc;
    }, []);
    set(
      state => {
        state.mediaData = data;
        state.mediaModels = models;
      },
      undefined,
      'getDocumentMedia'
    );
  },

  setSelectedMedia: (media: CacheDocument) => {
    set(
      state =>
        produce(state, draft => {
          if (state.selectedMedia.some(item => item.uuid === media.uuid)) {
            draft.selectedMedia = state.selectedMedia.filter(item => item.uuid !== media.uuid);
          } else {
            draft.selectedMedia.push(media);
          }
        }),
      undefined,
      'setSelectedMedia'
    );
  },

  setMedias: (media: CacheDocument[]) => {
    set(
      state => {
        state.selectedMedia = media;
      },
      undefined,
      'setMedias'
    );
  },

  setMediaFilters: async (filters: Filters) => {
    set(
      state => {
        state.mediaFilters = filters;
      },
      undefined,
      'setMediaFilters'
    );
  },

  setMediaFilter: async (key: string, value: FilterValue) => {
    set(
      state => {
        state.mediaFilters = initializePaginationFilters(
          {
            ...state.mediaFilters,
            [key]: value
          },
          initPaginationFiltersState,
          key
        );
      },
      undefined,
      'setMediaFilter'
    );
  },

  removeMediaFilter: async (key: string) => {
    const state = get();
    const stateFilters = {...state.mediaFilters};
    delete stateFilters[key];
    set({mediaFilters: initializePaginationFilters(stateFilters, initPaginationFiltersState, key)});
  },

  resetMediaFilters: () => {
    set(
      state => {
        state.mediaFilters = initialState.mediaFilters;
      },
      undefined,
      'resetMedia'
    );
  },

  editClaim: async (claimId: string, data: EditClaimFormValues) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiEditDocument(endpoints.apiDocuments, claimId, data, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
  },

  editClaimUsage: async (rmc: string, data: EditClaimUsageFormValues, action: string) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiEditClaimUsage(endpoints.apiDocuments, rmc, data, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]), action);
  },

  getValueOptions: async (uuid: Uuid, field: string, filters: {[key: string]: string} = {}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const options = await apiGetValueOptions<ValueOptionsApiResponse>(endpoints.apiDocuments, uuid, field, filters, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return mapValueOptionsResponse(options);
  },

  getValueOptionsRegionCountry: async (uuid: Uuid, field: string, filters: {[key: string]: string} = {}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const options = await apiGetValueOptions<ValueOptionsApiResponse>(endpoints.apiDocuments, uuid, field, filters, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return [{key: null, label: 'GLOBAL'}, ...mapValueOptionsRegionCountryResponse(options)];
  },

  getGmcVersions: async (uuid: Uuid, field: string, filters: {[key: string]: string} = {}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    set(
      state => {
        state.isGmcVersionLoading = true;
      },
      undefined,
      'getGmcVersions/pending'
    );
    const options = await apiGetValueOptions<ValueOptionsApiResponse>(endpoints.apiDocuments, uuid, field, filters, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    set(
      state => {
        state.isGmcVersionLoading = false;
      },
      undefined,
      'getGmcVersions/fulfilled'
    );
    return mapValueOptionsResponse(options);
  },

  editProductInformation: async (uuid: Uuid, data: EditProductInformationFormValues) => {
    const endpoints = getEndPoints();
    const sectionsPart = 'current';
    set(
      state => {
        if (state.sections[sectionsPart][uuid]?.information) {
          state.sections[sectionsPart][uuid].information.isEditLoading = true;
        }
      },
      undefined,
      'editProductInformation/pending'
    );

    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiEditDocument(endpoints.apiDocuments, uuid, data, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));

    set(
      state => {
        if (state.sections[sectionsPart][uuid]?.information) {
          state.sections[sectionsPart][uuid].information.isEditLoading = false;
        }
      },
      undefined,
      'editProductInformation/fulfilled'
    );
  },

  putProductInDraft: async (productId: string) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    await apiPutInDraft(endpoints.apiDocuments, productId, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
  },

  createNewRmc: async (gmc: string, values: RmcFormValue) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiCreateDocument(
      endpoints.apiDocuments,
      {globalMarketingContent: gmc, type: documentTypes.REGIONAL_MARKETING_CONTENT, ...values},
      stsCredentials,
      _get(scopedJwts, [JwtScope.WEBAPP])
    );
    return response;
  },

  getClaim: async (uuid: Uuid, hasThumbnail?: boolean, isIntention = false) => {
    const endpoints = getEndPoints();
    const key = isIntention ? 'claimIntentions' : 'claims';
    set(
      state => {
        state[key][uuid] = {
          isLoading: true,
          name: '',
          text: ''
        };
      },
      undefined,
      'getClaim/pending'
    );
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiGetDocument(endpoints.apiDocuments, uuid, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));

    if (hasThumbnail && (typeof response?.picto !== 'undefined' || typeof response?.thumbnail !== 'undefined')) {
      const preview = (await apiGetDocumentUrlByType(endpoints.apiDocuments, uuid, 'preview', stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]))).url;
      set(
        state => {
          state[key][uuid].thumbnail = preview;
        },
        undefined,
        'getClaim/setThumbnail'
      );
    }

    set(
      state => {
        state[key][uuid] = {
          ...state[key][uuid],
          isLoading: false,
          ...response
        };
      },
      undefined,
      'getClaim/fulfilled'
    );
    //TODO: Type response
    return response;
  },

  setValueWizardForm: (claimIntention: ClaimIntentions, intention: {key: string; label: string}, type: string, position: number, documentType: string) => {
    set(
      state => {
        state.valueWizardForm = {
          isOpen: true,
          type,
          defaultValue: claimIntention,
          intention,
          position,
          documentType
        };
      },
      undefined,
      'setValueWizardForm'
    );
  },

  resetValueWizardForm: () => {
    set(
      state => {
        state.valueWizardForm = initialState.valueWizardForm;
      },
      undefined,
      'resetValueWizardForm'
    );
  },

  resolveContextsUuid: async (data: ResolveContextsParams) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiResolveContextsUuid(endpoints.apiDocuments, data, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return response;
  },

  resetDocumentUuid: () => {
    set(
      state => {
        state.documents.current = initialState.documents.current;
        state.documents.compare = initialState.documents.compare;
      },
      undefined,
      'resetDocumentUuid'
    );
  },

  onShowOriginalChange: (section: SectionName, field: string, uuid: Uuid, isChecked: boolean) => {
    // Only on the left part of the ProductSheet compare
    const sectionsPart = 'current';
    set(
      state => {
        if (state.sections[sectionsPart][uuid][section]) {
          if (state.sections[sectionsPart][uuid][section]?.isShowOriginal) {
            state.sections[sectionsPart][uuid][section].isShowOriginal[field] = isChecked;
          } else {
            state.sections[sectionsPart][uuid][section].isShowOriginal = {[field]: true};
          }
        }
      },
      undefined,
      'onShowOriginalChange'
    );
  },

  setReorderedClaimIntentionTitle: (title: string) => {
    set(
      state => {
        state.reorderedClaimIntentionTitle = title;
      },
      undefined,
      'setReorderedClaimIntentionTitle'
    );
  },

  setHasReorderedParnuts: (has: boolean) => {
    set(
      state => {
        state.hasReorderedParnuts = has;
      },
      undefined,
      'setHasReorderedParnuts'
    );
  },

  replaceClaim: async (uuid: string, data: {claimId: string; replacedClaimId: string; country?: Uuid}) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiReplaceClaim(endpoints.apiDocuments, uuid, data, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return response;
  },

  generateClaims: async (uuid: Uuid, values: ClaimsFormValue) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiGenerateClaims(endpoints.apiDocuments, uuid, values, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return response;
  },

  getProductInformation: async (params: {uuid?: Uuid; mainItem?: string}): Promise<CacheDocument> => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();
    const response = await apiGetDocuments(endpoints.apiDocuments, {...params, typeId: 'product'}, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
    return response.data[0];
  },

  getSkusSection: async ({sectionsPart, uuid, skus}: {sectionsPart: SectionsPartName; uuid: string; skus: string[]}): Promise<void> => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    set(
      state => {
        if (!state.sections[sectionsPart][uuid]) {
          state.sections[sectionsPart][uuid] = {
            skus: {isLoading: true}
          };
        } else {
          state.sections[sectionsPart][uuid].skus = {
            isLoading: true
          };
        }
      },
      undefined,
      'getSkusSection/pending'
    );

    const {data} = await apiGetDocuments(endpoints.apiDocuments, {typeId: documentTypes.PRODUCT_SKU, uuid: skus}, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));

    set(
      state => {
        state.sections[sectionsPart][uuid].skus = {
          data,
          isLoading: false
        };
      },
      undefined,
      'getSkusSection/fulfilled'
    );
  },

  // Get the compositions list with possible languages for a given RMC uuid
  getCompositionsLanguages: async (rmcUuid: Uuid) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    const response = await apiGetValueOptions<ValueOptionsCompositionLanguagesApiResponse>(
      endpoints.apiDocuments,
      rmcUuid,
      'compositionLanguages',
      {},
      stsCredentials,
      _get(scopedJwts, [JwtScope.WEBAPP])
    );
    return mapValueOptionsCompositionLanguagesResponse(response);
  },

  createNewTranslationsOrder: async (rmcUuid: Uuid, orderType: OrderType, values: CreateNewTranslationsOrderValues) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    // Remove useless fields depending on the order type
    const {expectedDeliveryDate, ...rest} = orderType === ORDER_TYPE_LEGAL ? omit(values, ['claimsUsage', 'languages']) : omit(values, ['compositionLanguages']);
    const body = removeEmptyString({
      ...rest,
      expectedDeliveryDate: dayjs(expectedDeliveryDate).format(DATE_FORMAT),
      regionalMarketingContent: rmcUuid,
      type: translationsOrderTypesPath[orderType]
    });

    return await apiCreateDocument(endpoints.apiDocuments, body, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
  },

  getProductSection: async (productId: string) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    // Get the product information
    const response = await apiGetDocuments(
      endpoints.apiDocuments,
      {
        uuid: productId,
        typeId: 'product'
      },
      stsCredentials,
      _get(scopedJwts, [JwtScope.WEBAPP])
    );

    if (!response.data.length) {
      throw new Error('Product not found');
    }

    // The api returns an array of one element corresponding to the productId
    const productInformation = response.data[0];
    set(
      state => {
        state.documents.current.product = {
          ...(productInformation as ProductInfo),
          id: productInformation.uuid
        };
      },
      undefined,
      'getProductSection/setProductInformation'
    );
  },

  editTranslationOrder: async (orderUuid: Uuid, values: EditTranslationsOrderValues) => {
    const endpoints = getEndPoints();
    const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

    // Remove empty string values (if the user has emptied the field previously filled) by replacing them with null
    const body = Object.entries(values).reduce<Record<string, string | number | null>>((acc, currentValue) => {
      const [key, value] = currentValue;
      acc[key] = value === '' ? null : value;
      return acc;
    }, {});

    return await apiEditDocument(endpoints.apiDocuments, orderUuid, body, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));
  }
}));

export default useProductSheetStore;
